import React, { useCallback, useState } from 'react';
import { AppFeedbackProps } from './AppFeedbackProps';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import MultipleAttachments from '@shared/components/MultipleAttachments/MultipleAttachments';
import Button from '@shared/components/Button/Button';
import { Title, Text } from '@shared/components/typography';
import { TitleTag, TitleVariant } from '@shared/components/typography/Title';
import Loader from '@shared/components/Loader/Loader';
import FormInput from '@shared/components/FormInput/FormInput';
import { FeedbackForm, initValues, schema, values } from './AppFeedbackForm';
import FormSelect from '@shared/components/FormSelect/FormSelect';
import { AppFeedbackRequestData, FeedbackTypeEnum } from '@shared/models/feedback';
import { detectDeviceType } from '@shared/helpers/detectDevice';
import styles from './AppFeedback.module.scss';
import { FeedbackFormStatus } from '@shared/common/types/main';

const AppFeedback = (props: AppFeedbackProps) => {
  const { closeHandler, appFeedbackRequest, feedbackUploadFileRequest, userToken } = props;

  const [status, setStatus] = useState<FeedbackFormStatus>(FeedbackFormStatus.Init);
  const [files, setFiles] = useState<File[]>([]);

  const {
    register,
    control,
    handleSubmit,
  } = useForm<FeedbackForm>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: initValues,
  });

  const onSubmit = useCallback(
    async (data: FeedbackForm) => {
      if (!!!userToken) return;

      setStatus(FeedbackFormStatus.Loading);
      let requestData: AppFeedbackRequestData = {
        rate_description: data.message,
        feedback_type: data.type as FeedbackTypeEnum,
        rate_attachment_url: [],
        device_type: detectDeviceType(),
        browser_version: window.navigator.userAgent,
        organisation_id: null,
        submission_location: window.location.href,
      };

      const filesKeys = Object.keys(files);

      if (!!filesKeys.length) {
        const attachments = await Promise.all(
          filesKeys.map((file) => feedbackUploadFileRequest(files?.[+file], userToken)),
        );

        requestData = {
          ...requestData,
          rate_attachment_url: attachments.map(({ url }) => url),
        };
      }

      await appFeedbackRequest(requestData, userToken)
        .then(() => {
          setStatus(FeedbackFormStatus.Success);
        })
        .catch(() => {
          setStatus(FeedbackFormStatus.Init);
        });
    },
    [files, userToken],
  );

  return (
    <div>
      <Title classes="modal__title" tag={TitleTag.h3} variant={TitleVariant.section}>
        {status === FeedbackFormStatus.Success
          ? 'Thank You for Your Feedback'
          : 'What could have been better, or more helpful?'}
      </Title>
      {status === FeedbackFormStatus.Loading ? (
        <Loader />
      ) : status === FeedbackFormStatus.Init ? (
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormSelect
            control={control}
            name="type"
            options={values ?? []}
            label="Type*"
            menuPortal={false}
            closeMenuOnSelect={true}
            values={(values ?? []).find((x) => x.value === initValues.type)}
          />
          <FormInput
            label="Description"
            as="textarea"
            id="message"
            {...register('message')}
            placeholder={'Write your feedback here'}
            control={control}
          />

          <MultipleAttachments files={files} setFiles={setFiles} />

          <Button type="submit">Send</Button>
        </form>
      ) : (
        <>
          <Text classes={styles.text}>
            Thank you for sharing your feedback with us. Your feedback has been logged and will be reviewed by our team.
            <br />
            <br />
            We appreciate your input in helping us improve our system.
          </Text>
          <Button type="button" onClick={closeHandler}>
            Close
          </Button>
        </>
      )}
    </div>
  );
};

export default AppFeedback;
